import {motion} from 'framer-motion';
import {Separator} from 'platform/components';
import {Box, Clickable, Hide, HStack, Icon, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter, useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {Link} from 'react-router-dom';

import {always} from 'ramda';
import {isDate, isNumber} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {rentalOrderRoutes} from '@dms/routes';
import {getRentalOrderStateFlag} from '@dms/shared';

import {composePath, EMPTY_PLACEHOLDER, useToggle} from 'shared/web';

import {RentalOrderGridCard} from '../types/RentalOrderGridCard';
import {getColorStripePathByScheme} from '../utils/getColorStripePathByScheme';
import {KanbanCardWrapper} from './KanbanCardWrapper';

type RentalOrderKanbanCardProps = {
  card: RentalOrderGridCard;
};

export function RentalOrderKanbanCard(props: RentalOrderKanbanCardProps) {
  const [isPricingExpanded, togglePricing] = useToggle(false);

  const formatCurrency = useFormatCurrency();

  const formatDateTime = useDateTimeFormatter();
  const stateColorSchema = getRentalOrderStateFlag(props.card.state).colorScheme;
  const iconColor = getColorStripePathByScheme(stateColorSchema);
  const rentAndReturnDate =
    isDate(props.card.rentDate) && isDate(props.card.returnDate)
      ? `${formatDateTime('dateTimeShort', props.card.rentDate)} - ${formatDateTime('dateTimeShort', props.card.returnDate)}`
      : i18n.t('page.rentalOrder.labels.notPlannedYet');

  const redirectRoute = match(props.card.state)
    .with('draft', 'canceled', 'closed', always(rentalOrderRoutes.vehicleAndCustomer))
    .with('reserved', always(rentalOrderRoutes.rentVehicle))
    .with('rented', 'returned', always(rentalOrderRoutes.returnVehicle))
    .otherwise(always(rentalOrderRoutes.vehicleAndCustomer));

  return (
    <Link
      to={composePath(redirectRoute, {
        params: {id: props.card.id},
        queryParams: {source: 'rental-order-kanban'},
      })}
    >
      <KanbanCardWrapper color={stateColorSchema}>
        <Box padding={2}>
          <VStack spacing={2}>
            <VStack>
              <Text size="small" color="primary" alternative>
                {props.card.number || EMPTY_PLACEHOLDER}
              </Text>

              <HStack spacing={1} align="center">
                <Text size="small" color="primary">
                  {props.card.vehicleTitle || i18n.t('page.rentalOrder.labels.notPlannedYet')}
                </Text>
              </HStack>
            </VStack>

            <VStack spacing={1}>
              <Text size="xSmall" color="primary">
                <HStack align="center" spacing={1}>
                  <Icon size={4} value="sidebar/vehicles" color={iconColor} />
                  <Text size="xSmall" color="secondary">
                    {`${i18n.t('entity.vehicle.labels.licensePlate')}: ${props.card.licensePlate || EMPTY_PLACEHOLDER}`}
                  </Text>
                </HStack>
              </Text>

              <Text size="xSmall" color="primary">
                <HStack align="center" spacing={1}>
                  <Icon size={4} value="sidebar/task_management_calendar" color={iconColor} />
                  {rentAndReturnDate}
                </HStack>
              </Text>

              <Text size="xSmall" color="primary">
                <HStack align="center" spacing={1}>
                  <Icon size={4} value="sidebar/customers" color={iconColor} />
                  {props.card.customerName || i18n.t('page.rentalOrder.labels.notPlannedYet')}
                </HStack>
              </Text>
            </VStack>

            <VStack spacing={1}>
              <Box backgroundColor="palettes.neutral.30.100" borderRadius="small" padding={1}>
                <Clickable
                  onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    togglePricing();
                  }}
                >
                  <HStack spacing={1} align="center" justify="space-between">
                    <Text size="xSmall">{i18n.t('page.rentalOrder.labels.pricingDetails')}</Text>
                    <Icon
                      size={3}
                      value={
                        isPricingExpanded ? 'navigation/arrow_upward' : 'navigation/arrow_downward'
                      }
                    />
                  </HStack>
                </Clickable>

                <motion.div
                  initial={false}
                  animate={{
                    height: isPricingExpanded ? 'auto' : 0,
                    opacity: isPricingExpanded ? 1 : 0,
                  }}
                  transition={{duration: 0.3}}
                  style={{overflow: 'hidden'}}
                >
                  <Separator spacing={1} />

                  <VStack spacing={2}>
                    <HStack spacing={1}>
                      <Text size="xSmall" color="secondary">
                        {`${i18n.t('page.rentalOrder.labels.dailyRate')}:`}
                      </Text>
                      <Text size="xSmall">
                        {isNumber(props.card.dailyRate?.amount)
                          ? formatCurrency(
                              props.card.dailyRate.amount,
                              props.card.dailyRate.currency
                            )
                          : EMPTY_PLACEHOLDER}
                      </Text>
                    </HStack>
                    <Hide when={props.card.state === 'draft'}>
                      <HStack spacing={1}>
                        <Text
                          size="xSmall"
                          color="secondary"
                        >{`${i18n.t('page.rentalOrder.labels.expectedPrice')}:`}</Text>
                        <Text size="xSmall">
                          {isNumber(props.card.expectedPriceWithVat?.amount)
                            ? formatCurrency(
                                props.card.expectedPriceWithVat.amount,
                                props.card.expectedPriceWithVat.currency
                              )
                            : EMPTY_PLACEHOLDER}
                        </Text>
                      </HStack>

                      <Hide when={props.card.state === 'reserved'}>
                        <HStack spacing={1}>
                          <Text size="xSmall" color="secondary">
                            {`${i18n.t('page.rentalOrder.labels.salePrice')}:`}
                          </Text>
                          <Text size="xSmall">
                            {isNumber(props.card.salePriceWithVat?.amount)
                              ? formatCurrency(
                                  props.card.salePriceWithVat.amount,
                                  props.card.salePriceWithVat.currency
                                )
                              : EMPTY_PLACEHOLDER}
                          </Text>
                        </HStack>
                      </Hide>
                    </Hide>
                  </VStack>
                </motion.div>
              </Box>
            </VStack>
          </VStack>
        </Box>
      </KanbanCardWrapper>
    </Link>
  );
}
